/**
 * @saas/shared — typy + stałe dla menu buildera (ADR-0031).
 *
 * Używane przez:
 *   - backend (apps/api/lib/menu/*, apps/api/app/api/menu/*) — walidator, source endpoint.
 *   - frontend (apps/admin/src/components/menu/*, hooks) — renderer hierarchy, paletka.
 *
 * Jedno źródło prawdy dla 6 stałych system_items (Users / Employees / Companies /
 * Contractors / ContractorContacts / Settings). Backend wstrzymuje listę
 * w GET /api/menu/source, frontend renderuje przez wspólny mapping.
 */

import { z } from 'zod';

import type { FilterConfig, ListConfig, SortConfig } from './list-view';
import { filterConfigSchema, listConfigSchema, sortConfigSchema } from './list-view';
import type { ModuleTemplateFormConfig } from './module-builder-views';
import { moduleTemplateFormConfigSchema } from './module-builder-views';

// ---------------------------------------------------------------------------
// Hierarchy types
// ---------------------------------------------------------------------------

export type MenuItemType =
  | 'module'
  | 'system_item'
  | 'custom_link'
  | 'section'
  | 'separator'
  | 'installed_area';

export type SystemItemKey =
  | 'users'
  | 'employees'
  | 'companies'
  | 'contractors'
  | 'contractor_contacts'
  | 'settings';

/**
 * Wbudowany widok pozycji menu (embed) — tylko dla type='module'.
 * Umożliwia to by ta sama kartoteka wisiała w menu wiele razy,
 * każde wejście z własnym widokiem listy / formularza / filtrem bazowym.
 *
 * F1: model + walidacja. Bez zmian w runtime, UI ani instalacji.
 */
export interface MenuItemView {
  listConfig?: ListConfig;
  formConfig?: ModuleTemplateFormConfig;
  baseFilters?: FilterConfig[];
  /** Stałe sortowanie aplikowane zawsze przy wejściu na listę (niezależnie od preferencji usera). */
  baseSort?: SortConfig[];
  /** Wersja schema embeda — rezerwa na migracje kształtu. */
  v?: 1;
}

export interface MenuItem {
  /** UUID v4 — unique tree-wide. */
  id: string;
  type: MenuItemType;

  // type='module' — catalog subscription LUB custom user module
  moduleCode?: string;

  // type='system_item' — whitelist SystemItemKey
  itemKey?: SystemItemKey;

  // type='installed_area' — kod obszaru (module_areas.code w ADM); sekcja renderuje
  // rodzaje danych z zainstalowanych modułów tego obszaru (zawartość z
  // GET /api/my/installed-modules, gating po subskrypcji robi backend)
  areaCode?: string;

  // type='custom_link'
  url?: string;
  /** target=_blank gdy true. */
  external?: boolean;

  // type='module' | 'system_item' | 'custom_link' | 'section'
  /** Override etykiety. Dla module/system_item null = label z definicji modułu/system_item. */
  label?: string;
  /** Override ikony Lucide. Dla module/system_item null = ikona z definicji. */
  iconName?: string;

  // type='section'
  /** Default state collapsed/expanded. User toggle zapisuje localStorage per user. */
  collapsed?: boolean;
  children?: MenuItem[];

  // type='module' only — opcjonalny wbudowany widok (embed). Wzajemnie wykluczający
  // z viewRef. Walidator egzekwuje dozwolony typ + wykluczenie.
  /** Wbudowany widok pozycji menu (embed). Dozwolony tylko dla type='module'. */
  view?: MenuItemView;
  /**
   * Referencja do zewnętrznego widoku (UUID). Rezerwa pod przyszły offload konfiguracji
   * widoku do osobnego rekordu. Dozwolone tylko dla type='module'.
   * Wzajemnie wykluczające z `view`.
   */
  viewRef?: string;
  /**
   * Referencja do nazwanego layoutu LISTY — format 'entitySlug:viewSlug'.
   * Analog `viewRef` dla formularza: gdy ustawione, listConfig żyje w entity.views
   * (type='list') zamiast inline w `view.listConfig`. Dozwolone tylko dla type='module'.
   */
  listRef?: string;
}

// ---------------------------------------------------------------------------
// System items — kanoniczna lista 6 stałych pozycji
// ---------------------------------------------------------------------------

export interface SystemItemDefinition {
  itemKey: SystemItemKey;
  /** Domyślna etykieta (i18n key fallback). */
  defaultLabel: string;
  /** i18n key — preferowane nad defaultLabel gdy frontend ma dostęp do t(). */
  labelI18nKey: string;
  /** Lucide icon component name (z apps/admin/src/components/Sidebar.tsx ICON_MAP). */
  iconName: string;
  /** Route w admin SPA (tenant scope). */
  path: string;
}

export const SYSTEM_ITEMS: ReadonlyArray<SystemItemDefinition> = [
  {
    itemKey: 'users',
    defaultLabel: 'Użytkownicy',
    labelI18nKey: 'tenant.sidebar.users',
    iconName: 'UserCog',
    path: '/users',
  },
  {
    itemKey: 'employees',
    defaultLabel: 'Pracownicy',
    labelI18nKey: 'tenant.sidebar.employees',
    iconName: 'BadgeCheck',
    path: '/employees',
  },
  {
    itemKey: 'companies',
    defaultLabel: 'Firmy',
    labelI18nKey: 'tenant.sidebar.companies',
    iconName: 'Building2',
    path: '/companies',
  },
  {
    itemKey: 'contractors',
    defaultLabel: 'Kontrahenci',
    labelI18nKey: 'tenant.sidebar.contractors',
    iconName: 'Briefcase',
    path: '/contractors',
  },
  {
    itemKey: 'contractor_contacts',
    defaultLabel: 'Kontakty kontrahentów',
    labelI18nKey: 'tenant.sidebar.contractorContacts',
    iconName: 'Contact',
    path: '/contractor-contacts',
  },
  {
    itemKey: 'settings',
    defaultLabel: 'Ustawienia',
    labelI18nKey: 'tenant.sidebar.settings',
    iconName: 'Settings',
    path: '/settings',
  },
];

export const SYSTEM_ITEM_KEYS: ReadonlyArray<SystemItemKey> = SYSTEM_ITEMS.map((s) => s.itemKey);

// ---------------------------------------------------------------------------
// Kartoteki dokumentów — klasy dokumentów jako kartoteki pakietu BAZOWEGO
// ---------------------------------------------------------------------------

/** Slug kartoteki systemowej dokumentu (routing przez MODULE_LANDING_OVERRIDES w Sidebar). */
export type DocumentKartotekaSlug =
  | 'purchase_invoices'
  | 'sale_invoices'
  | 'contracts'
  | 'letters'
  | 'commercial'
  | 'internal'
  | 'specjalne';

export interface DocumentKartotekaDefinition {
  /** Klasa dokumentu — klucz bramki dostępu (tabela module_document_classes). */
  documentClass:
    | 'purchase_invoice'
    | 'sale_invoice'
    | 'contract'
    | 'letter'
    | 'commercial'
    | 'internal'
    | 'specjalne';
  /** Slug kartoteki systemowej (spójny z MODULE_LANDING_OVERRIDES + palety). */
  slug: DocumentKartotekaSlug;
  /** Etykieta PL (paleta kreatora + domyślna nazwa pozycji po dodaniu do menu). */
  label: string;
  /** Nazwa ikony Lucide (ICON_MAP w Sidebar). */
  iconName: string;
  /** Trasa docelowa w admin SPA (tenant scope). */
  path: string;
}

/**
 * Kanoniczne 7 kartotek dokumentów. Po restrukturyzacji „dokumenty bez modułu" (ADR-0083)
 * klasy dokumentów należą do pakietu BAZOWEGO (crm_core) — nie są osobnymi modułami. Każdy
 * tenant z podstawową subskrypcją ma je zawsze. Ta lista to jedyne źródło prawdy dla:
 *   - palety kreatora menu tenanta (`/api/menu/source`) — tenant sam układa z nich menu,
 *   - sidebara (menu customowe) — te slugi są „aktywne" gdy tenant ma crm_core.
 * NIE trafiają do widoku domyślnego automatycznie — pojawiają się dopiero, gdy tenant
 * świadomie doda je do własnego menu (kreator). Routing: slug → path (MODULE_LANDING_OVERRIDES).
 */
export const DOCUMENT_KARTOTEKI: ReadonlyArray<DocumentKartotekaDefinition> = [
  {
    documentClass: 'purchase_invoice',
    slug: 'purchase_invoices',
    label: 'Faktury zakupowe',
    iconName: 'FileText',
    path: '/purchase-invoices',
  },
  {
    documentClass: 'sale_invoice',
    slug: 'sale_invoices',
    label: 'Faktury sprzedażowe',
    iconName: 'Receipt',
    path: '/sale-invoices',
  },
  {
    documentClass: 'contract',
    slug: 'contracts',
    label: 'Umowy',
    iconName: 'File',
    path: '/contracts',
  },
  {
    documentClass: 'letter',
    slug: 'letters',
    label: 'Pisma',
    iconName: 'Mail',
    path: '/letters',
  },
  {
    documentClass: 'commercial',
    slug: 'commercial',
    label: 'Dokumenty handlowe',
    iconName: 'FileText',
    path: '/commercial-docs',
  },
  {
    documentClass: 'internal',
    slug: 'internal',
    label: 'Dokumenty wewnętrzne',
    iconName: 'Shield',
    path: '/internal-docs',
  },
  {
    documentClass: 'specjalne',
    slug: 'specjalne',
    label: 'Dokumenty specjalne',
    iconName: 'Star',
    path: '/special-docs',
  },
];

/** Zbiór slugów kartotek dokumentów — szybki lookup w sidebarze (active-check). */
export const DOCUMENT_KARTOTEKA_SLUGS: ReadonlySet<string> = new Set(
  DOCUMENT_KARTOTEKI.map((d) => d.slug),
);

// ---------------------------------------------------------------------------
// Kartoteki pakietu BAZOWEGO (crm_core) — dostępne dla KAŻDEGO tenanta
// ---------------------------------------------------------------------------

/**
 * Definicja kartoteki systemowej pakietu bazowego (bez rozróżnienia klasy dokumentu).
 * Wspólny kształt dla dokumentów i innych kartotek bazowych (Towary).
 */
export interface BaseKartotekaDefinition {
  /** Slug kartoteki systemowej (spójny z MODULE_LANDING_OVERRIDES + palety). */
  slug: string;
  /** Etykieta PL (paleta kreatora + domyślna nazwa pozycji po dodaniu do menu). */
  label: string;
  /** Nazwa ikony Lucide (ICON_MAP w Sidebar). */
  iconName: string;
  /** Trasa docelowa w admin SPA (tenant scope). */
  path: string;
}

/**
 * Kartoteki pakietu BAZOWEGO dostępne dla KAŻDEGO tenanta z crm_core (ADR-0083) — 7 klas
 * dokumentów + Towary. Wszystkie mają działający ekran tenanta (Towary → /products, dokumenty
 * → dedykowane trasy) i bramkę dostępu na crm_core. Jedyne źródło prawdy dla:
 *   - palety kreatora menu (`/api/menu/source`) — tenant sam układa z nich menu,
 *   - sidebara (menu customowe) — widoczne gdy tenant ma ich moduł-właściciel, inaczej ukryte.
 * NIE trafiają do widoku domyślnego automatycznie — pojawiają się dopiero gdy tenant je doda.
 *
 * Magazyny (warehouses) są na liście mimo braku dedykowanego ekranu przeglądania (decyzja
 * Eugenii 2026-08-02): to encja zaplecza (stany towarów + selektor w dokumentach), prowadzi do
 * generycznej trasy `/modules/warehouses`. Ich modułem-właścicielem jest `warehouses`, nie
 * crm_core (`SYSTEM_KARTOTEKA_TARGETS` w menu-targets.ts) — paleta i sidebar pokazują je
 * tylko przy tej subskrypcji, bez niej są ukryte (trasa i tak zwraca 404).
 */
export const BASE_PACKAGE_KARTOTEKI: ReadonlyArray<BaseKartotekaDefinition> = [
  ...DOCUMENT_KARTOTEKI.map((d) => ({
    slug: d.slug,
    label: d.label,
    iconName: d.iconName,
    path: d.path,
  })),
  { slug: 'products', label: 'Towary', iconName: 'Package', path: '/products' },
  { slug: 'warehouses', label: 'Magazyny', iconName: 'Warehouse', path: '/modules/warehouses' },
];

/** Zbiór slugów kartotek pakietu bazowego — szybki lookup w sidebarze (active-check). */
export const BASE_PACKAGE_KARTOTEKA_SLUGS: ReadonlySet<string> = new Set(
  BASE_PACKAGE_KARTOTEKI.map((d) => d.slug),
);

/**
 * Moduły-FUNKCJE tenanta: jeden ekran/akcja, nie kartoteka danych ani nawigowalny widok
 * menu. Ich menu domyślne to pojedynczy link (Procesy → „Decyzje"), więc jako osobny widok
 * w przełączniku ALBO osobny szablon w kreatorze menu tylko dublowałyby swoją jedyną pozycję.
 * Wykluczane z:
 *   - przełącznika widoku menu w sidebarze (renderowane jako linki przypięte w stopce),
 *   - listy „MENU SYSTEMOWE" w kreatorze menu (nie są szablonem do zduplikowania).
 * Jedno źródło prawdy dla obu miejsc (decyzja 2026-08-01) — nie hardkodujemy w dwóch plikach.
 *
 * KSeF (ksef_integration) NIE jest już funkcją (2026-09-09): wystawia kartotekę „Dokumenty
 * KSeF" (lista faktur z /ksef), więc jest zwykłą kartoteką pakietowaną subskrypcją modułu —
 * konfiguracja połączeń została w Ustawieniach → Integracje, osobno od listy dokumentów.
 */
export const MENU_FUNCTION_MODULE_CODES: ReadonlySet<string> = new Set(['processes']);

// ---------------------------------------------------------------------------
// Walidator limits (egzekwowane w apps/api/lib/menu/validator.ts)
// ---------------------------------------------------------------------------

export const MENU_LIMITS = {
  /** Max liczba elementów na top-level. */
  MAX_TOP_LEVEL_ITEMS: 200,
  /** Max głębokość zagnieżdżania (1 = sama sekcja bez dzieci, 5 = pięć poziomów). */
  MAX_DEPTH: 5,
  /** Max rozmiar zserializowanego JSON-a — chroni przed body-size DoS. */
  MAX_SERIALIZED_BYTES: 1_048_576, // 1 MB
} as const;

// ---------------------------------------------------------------------------
// Adres odnośnika (`custom_link.url`) — wspólna reguła walidatora i renderera
// ---------------------------------------------------------------------------

/** Sztuczny origin do sprawdzenia, czy adres względny zostaje na tym samym hoście. */
const MENU_LINK_PROBE_ORIGIN = 'https://menu-link.invalid';

export type MenuLinkUrlKind = 'relative' | 'absolute';

/**
 * Klasyfikuje adres odnośnika w menu: `absolute` (http/https), `relative` (ścieżka w aplikacji)
 * albo `null` (niedozwolony).
 *
 * Adres względny musi po rozwinięciu przez parser URL zostać na tym samym hoście. Samo
 * `startsWith('/')` nie wystarcza: `//obca.pl`, `/\obca.pl` i `/<TAB>/obca.pl` przeglądarka
 * (i React Router) traktuje jak adres zewnętrzny — kliknięcie w pasku bocznym prowadziłoby
 * na obcą stronę (otwarte przekierowanie). Schematy `javascript:`/`data:` są odrzucane.
 */
export function classifyMenuLinkUrl(url: string): MenuLinkUrlKind | null {
  if (url.startsWith('http://') || url.startsWith('https://')) return 'absolute';
  if (!url.startsWith('/')) return null;
  try {
    return new URL(url, MENU_LINK_PROBE_ORIGIN).origin === MENU_LINK_PROBE_ORIGIN
      ? 'relative'
      : null;
  } catch {
    return null;
  }
}

/**
 * Max rozmiar (bajty UTF-8) zserializowanego embeda `view` pojedynczej pozycji menu.
 * Guard uruchamiany w walidatorze PRZED sprawdzeniem limitu 1 MB całości — pozwala
 * zwrócić czytelny komunikat zanim cały payload zostanie zmierzony.
 *
 * 64 KB = sensowna górna granica dla konfiguracji widoku jednej kartoteki
 * (setki kolumn + filtry + formConfig mieszczą się z zapasem).
 */
export const MAX_MENU_VIEW_BYTES = 64 * 1024; // 65 536 B

/**
 * Zod schema wbudowanego widoku pozycji menu.
 * Eksportowana z @saas/shared żeby walidator backendu (apps/api/lib/menu/validator.ts)
 * mógł ją reużyć bez duplikowania kształtu.
 */
export const menuItemViewSchema = z.object({
  listConfig: listConfigSchema.optional(),
  formConfig: moduleTemplateFormConfigSchema.optional(),
  baseFilters: z.array(filterConfigSchema).optional(),
  baseSort: z.array(sortConfigSchema).optional(),
  v: z.literal(1).optional(),
});

// ---------------------------------------------------------------------------
// API response shapes
// ---------------------------------------------------------------------------

export interface MenuSourceModule {
  /** Code modułu z subskrypcji (catalog) — np. 'crm_core', 'workflow'. */
  moduleCode: string;
  label: string;
  iconName: string | null;
  status: 'active' | 'trialing';
}

export interface MenuSourceCustomModule {
  /** Code modułu utworzonego przez admina tenanta (F12.A). */
  moduleCode: string;
  label: string;
  iconName: string | null;
}

export interface MenuSourceInstalledArea {
  /** Kod obszaru (module_areas.code w ADM) grupującego zainstalowane moduły. */
  areaCode: string;
  label: string;
  iconName: string | null;
}

/** Moduł (wykupiony) używający kartoteki — chip w palecie kartotek. */
export interface MenuSourceKartotekaModule {
  code: string;
  name: string;
}

/**
 * Kartoteka w palecie buildera menu tenanta (kreator menu, decyzja 2026-07-02:
 * paleta = płaska lista kartotek z wykupionych modułów, nie moduły/obszary).
 * Wstawiana do hierarchii jako `MenuItem` typu `module` z `moduleCode = slug` —
 * sidebar rozwiązuje ją na żywo (wygasły moduł → pozycja nieaktywna, decyzja D2).
 */
export interface MenuSourceKartoteka {
  /** 'system' = kartoteka systemowa platformy; 'custom' = rodzaj danych klienta. */
  kind: 'system' | 'custom';
  slug: string;
  label: string;
  iconName: string | null;
  /**
   * Trasa wewnętrzna — obecna gdy kartoteka systemowa żyje w menu modułu jako
   * pozycja-link (np. `/employees`, `/contracts`); paleta wstawia ją wtedy jako
   * `custom_link` (renderer ma NavLink dla tras względnych). Brak = wstawiana
   * jako pozycja `module` po slugu.
   */
  path?: string;
  /** Wykupione moduły, których menu zawiera tę kartotekę (chipy). Pusta = własna klienta. */
  usedInModules: MenuSourceKartotekaModule[];
}

/**
 * Pozycja FUNKCJONALNA w palecie buildera menu tenanta — wewnętrzny link z menu
 * modułu, który NIE jest kartoteką (np. „Decyzje" z modułu Procesy → ekran akceptacji).
 * Wstawiana do hierarchii jako `custom_link` (renderer ma NavLink dla tras względnych).
 * Trzymana osobno od kartotek (decyzja 2026-07-21: procesy/narzędzia ≠ kartoteka).
 */
export interface MenuSourceFunctionalItem {
  /** Stabilny identyfikator wyprowadzony z trasy (np. `processes-decisions-pending`). */
  slug: string;
  label: string;
  iconName: string | null;
  /** Trasa wewnętrzna (zaczyna się od `/`). */
  path: string;
  /** Wykupione moduły, których menu zawiera tę pozycję (chipy). */
  usedInModules: MenuSourceKartotekaModule[];
}

export interface MenuSourceResponse {
  /** Aktywne moduły z subskrypcji catalog. */
  modules: MenuSourceModule[];
  /** Moduły niestandardowe (custom modules z show_in_menu=true). */
  customModules: MenuSourceCustomModule[];
  /** 6 stałych pozycji systemowych. */
  systemItems: SystemItemDefinition[];
  /**
   * Obszary zainstalowanych modułów (hub Kreatora, Krok 4) — źródło elementu
   * `installed_area`. Tylko obszary, w których klient ma opłacone moduły z
   * widocznymi rodzajami danych. Opcjonalne dla zgodności starszych mocków.
   */
  installedAreas?: MenuSourceInstalledArea[];
  /**
   * Płaska lista kartotek z wykupionych modułów + własnych klienta (kreator menu
   * tenanta). Gdy obecna i niepusta — paleta pokazuje kartoteki ZAMIAST sekcji
   * modułów/obszarów. Opcjonalne dla zgodności starszych mocków.
   */
  kartoteki?: MenuSourceKartoteka[];
  /**
   * Pozycje funkcjonalne (wewnętrzne linki z menu modułów, które NIE są kartotekami —
   * np. „Decyzje" z Procesów). Paleta pokazuje je w osobnej grupie „Funkcje" i wstawia
   * jako `custom_link`. Opcjonalne dla zgodności starszych mocków.
   */
  functional?: MenuSourceFunctionalItem[];
}

/**
 * Kolory ikony menu customowego — ZAMKNIĘTA paleta (spójny wygląd paska bocznego, brak dowolnego
 * hex-a). Zapisywane jako klucz; mapowanie na barwę żyje we froncie (barwy logo Workflow365).
 */
export const MENU_COLORS = ['blue', 'green', 'orange', 'violet', 'teal', 'pink', 'amber'] as const;
export type MenuColor = (typeof MENU_COLORS)[number];

export interface MenuLayoutDto {
  id: string;
  name: string;
  description: string | null;
  /** Nazwa ikony menu (lucide); null/brak = ikona domyślna. */
  icon?: string | null;
  /** Kolor ikony (klucz z `MENU_COLORS`); null/brak = kolor wg kolejności w pasku. */
  color?: MenuColor | null;
  /** Nieaktywne menu znika z paska bocznego (zostaje w kreatorze). Brak = aktywne. */
  isActive?: boolean;
  hierarchy: MenuItem[];
  isDefault: boolean;
  /** OCC counter — frontend wysyła w PATCH body jako expectedVersion. ADR-0031 Etap B. */
  version: number;
  updatedAt: string;
}

/**
 * Lekkie metadane layoutu — używane przez GET /api/menu/layouts dla dropdownu
 * (bez `hierarchy` żeby nie ciągać 1MB JSONB per layout w liście). Pełny layout
 * dociągany przez GET /api/menu/layouts/:id przy switch.
 */
export interface MenuLayoutSummary {
  id: string;
  name: string;
  /** Nazwa ikony menu (lucide); null/brak = ikona domyślna. */
  icon?: string | null;
  /** Kolor ikony (klucz z `MENU_COLORS`); null/brak = kolor wg kolejności w pasku. */
  color?: MenuColor | null;
  /** Nieaktywne menu znika z paska bocznego. Brak (stare mocki) = aktywne. */
  isActive?: boolean;
  isDefault: boolean;
  version: number;
  updatedAt: string;
  /**
   * Puste menu (hierarchy = []) — galeria ukrywa takie układy (np. seedowany
   * pusty „Default") i pokazuje empty-state z przyciskiem tworzenia.
   * Opcjonalne dla zgodności starszych mocków.
   */
  isEmpty?: boolean;
}

// ===========================================================================
// Admin menu builder (ADR-0034).
//
// Odrębne typy od tenant menu — admin scope nie ma subskrypcji modułów ani
// custom modules. Paletka admina to wyłącznie 16 stałych pozycji systemowych
// (analog ADMIN_MENU z apps/admin/src/config/menu.ts), custom linki, sekcje,
// separatory.
//
// Walidator backend (apps/api/lib/admin-menu/validator.ts) jest osobny od
// tenant validatora — świadoma duplikacja zamiast generic, żeby tenant kod
// pozostał nietknięty przy zmianach na admin scope.
// ===========================================================================

/**
 * 4-value ENUM ról admin scope (z `admin_users.role` w ADM schema).
 * Używany przez:
 *   - backend: walidacja `adminMenuLayoutRoleAssignments.role` (CHECK constraint).
 *   - frontend: LayoutRolesModal wybór ról + AdminLayoutSwitcherFooter filter.
 *
 * Single source of truth — backend `apps/api/drizzle/adm/schema.ts` re-eksportuje
 * tę samą stałą, frontend importuje stąd dla LayoutRolesModal i admin walidatora.
 */
export const ADMIN_ROLES = ['super_admin', 'support', 'finance', 'readonly'] as const;
export type AdminRole = (typeof ADMIN_ROLES)[number];

export type AdminMenuItemType = 'system_item' | 'custom_link' | 'section' | 'separator';

export type AdminSystemItemKey =
  | 'dashboard'
  | 'modules'
  | 'module_pricing'
  | 'bundles'
  | 'db_pools'
  | 'tenants'
  | 'subscriptions'
  | 'invoices'
  | 'coupons'
  | 'admin_users'
  | 'audit_log'
  | 'bug_reports'
  | 'module_templates'
  | 'kartoteki'
  | 'global_document_types'
  | 'global_dictionaries'
  | 'process_templates'
  | 'menu_builder';

export interface AdminMenuItem {
  /** UUID v4 — unique tree-wide. */
  id: string;
  type: AdminMenuItemType;

  // type='system_item' — whitelist AdminSystemItemKey
  itemKey?: AdminSystemItemKey;

  // type='custom_link'
  url?: string;
  /** target=_blank gdy true. */
  external?: boolean;

  // type='system_item' | 'custom_link' | 'section'
  /** Override etykiety. Dla system_item null = label z definicji. */
  label?: string;
  /** Override ikony Lucide. Dla system_item null = ikona z definicji. */
  iconName?: string;

  // type='section'
  /** Default state collapsed/expanded. User toggle zapisuje localStorage per user. */
  collapsed?: boolean;
  children?: AdminMenuItem[];
}

export interface AdminSystemItemDefinition {
  itemKey: AdminSystemItemKey;
  /** Domyślna etykieta (i18n fallback). */
  defaultLabel: string;
  /** i18n key — preferowane nad defaultLabel gdy frontend ma dostęp do t(). */
  labelI18nKey: string;
  /** Lucide icon component name (z apps/admin/src/components/Sidebar.tsx ICON_MAP). */
  iconName: string;
  /** Route w admin SPA (admin scope). */
  path: string;
  /** Kod uprawnienia admina wymagany do widoczności pozycji (też w menu z Edytora menu). */
  requiredPermission?: string;
}

/**
 * 19 stałych pozycji menu administratora ITE.
 *
 * Skopiowane z apps/admin/src/config/menu.ts ADMIN_MENU (F2 MVP static list).
 * Jeśli ADMIN_MENU dostanie nową pozycję — dorzucić tu również, w paletce
 * builder buduje na tej liście.
 */
export const ADMIN_SYSTEM_ITEMS: ReadonlyArray<AdminSystemItemDefinition> = [
  {
    itemKey: 'dashboard',
    defaultLabel: 'Dashboard',
    labelI18nKey: 'admin.sidebar.dashboard',
    iconName: 'LayoutDashboard',
    path: '/dashboard',
  },
  {
    itemKey: 'modules',
    defaultLabel: 'Moduły',
    labelI18nKey: 'admin.sidebar.modules',
    iconName: 'Package',
    path: '/modules',
  },
  {
    itemKey: 'module_pricing',
    defaultLabel: 'Cennik modułów',
    labelI18nKey: 'admin.sidebar.modulePricing',
    iconName: 'BadgeDollarSign',
    path: '/module-pricing',
  },
  {
    itemKey: 'bundles',
    defaultLabel: 'Pakiety',
    labelI18nKey: 'admin.sidebar.bundles',
    iconName: 'Package',
    path: '/bundles',
  },
  {
    itemKey: 'db_pools',
    defaultLabel: 'Pule baz',
    labelI18nKey: 'admin.sidebar.dbPools',
    iconName: 'Database',
    path: '/db-pools',
  },
  {
    itemKey: 'tenants',
    defaultLabel: 'Klienci',
    labelI18nKey: 'admin.sidebar.tenants',
    iconName: 'Building2',
    path: '/tenants',
  },
  {
    itemKey: 'subscriptions',
    defaultLabel: 'Subskrypcje',
    labelI18nKey: 'admin.sidebar.subscriptions',
    iconName: 'Receipt',
    path: '/subscriptions',
  },
  {
    itemKey: 'invoices',
    defaultLabel: 'Faktury',
    labelI18nKey: 'admin.sidebar.invoices',
    iconName: 'FileText',
    path: '/invoices',
  },
  {
    itemKey: 'coupons',
    defaultLabel: 'Kupony',
    labelI18nKey: 'admin.sidebar.coupons',
    iconName: 'Ticket',
    path: '/coupons',
  },
  {
    itemKey: 'admin_users',
    defaultLabel: 'Użytkownicy Panelu Admin',
    labelI18nKey: 'admin.sidebar.adminUsers',
    iconName: 'ShieldCheck',
    path: '/admin-users',
  },
  {
    itemKey: 'audit_log',
    defaultLabel: 'Log zdarzeń',
    labelI18nKey: 'admin.sidebar.auditLog',
    iconName: 'ScrollText',
    path: '/audit-log',
  },
  {
    // Zgłoszenia błędów od użytkowników tenantów (widget „Zgłoś błąd").
    itemKey: 'bug_reports',
    defaultLabel: 'Zgłoszenia',
    labelI18nKey: 'admin.sidebar.bugReports',
    iconName: 'Bug',
    path: '/bug-reports',
  },
  {
    // Kreator modułów (hub) — brakował w palecie edytora menu admina
    // (feedback Eugenii 2026-07-03); klucze walidatora liczą się z tej listy.
    itemKey: 'module_templates',
    defaultLabel: 'Kreator modułów',
    labelI18nKey: 'admin.sidebar.moduleTemplates',
    iconName: 'LayoutTemplate',
    path: '/module-templates',
  },
  {
    // Kartoteki / Globalne typy dokumentów / Szablony procesów — brakowały w
    // palecie edytora menu admina mimo obecności w ADMIN_MENU (rozjazd wykryty
    // 2026-07-04). Globalne słowniki dołożone tym samym torem (2026-07-06).
    itemKey: 'kartoteki',
    defaultLabel: 'Kartoteki',
    labelI18nKey: 'admin.sidebar.kartoteki',
    iconName: 'Library',
    path: '/kartoteki',
  },
  {
    itemKey: 'global_document_types',
    defaultLabel: 'Globalne typy dokumentów',
    labelI18nKey: 'admin.sidebar.globalDocumentTypes',
    iconName: 'FileStack',
    path: '/global-document-types',
  },
  {
    itemKey: 'global_dictionaries',
    defaultLabel: 'Globalne słowniki',
    labelI18nKey: 'admin.sidebar.globalDictionaries',
    iconName: 'ListTree',
    path: '/global-dictionaries',
  },
  {
    itemKey: 'process_templates',
    defaultLabel: 'Szablony procesów',
    labelI18nKey: 'admin.sidebar.processTemplates',
    iconName: 'GitBranch',
    path: '/process-templates',
  },
  {
    itemKey: 'menu_builder',
    defaultLabel: 'Edytor menu',
    labelI18nKey: 'admin.sidebar.menuBuilder',
    iconName: 'MenuSquare',
    path: '/menu-builder',
  },
];

export const ADMIN_SYSTEM_ITEM_KEYS: ReadonlyArray<AdminSystemItemKey> = ADMIN_SYSTEM_ITEMS.map(
  (s) => s.itemKey,
);

// Admin paletka — tylko system_items + custom_links + sekcje + separatory.
// Brak modules / customModules bo admin operator ITE nie ma subskrypcji.
export interface AdminMenuSourceResponse {
  systemItems: AdminSystemItemDefinition[];
}

export interface AdminMenuLayoutDto {
  id: string;
  name: string;
  description: string | null;
  /** Nazwa ikony menu (lucide); null/brak = ikona domyślna. */
  icon?: string | null;
  /** Kolor ikony (klucz z `MENU_COLORS`); null/brak = kolor domyślny. */
  color?: MenuColor | null;
  /** Nieaktywne menu jest pomijane przy wyborze menu panelu. Brak = aktywne. */
  isActive?: boolean;
  hierarchy: AdminMenuItem[];
  isDefault: boolean;
  /** OCC counter — frontend wysyła w PATCH body jako expectedVersion. */
  version: number;
  updatedAt: string;
}

export interface AdminMenuLayoutSummary {
  id: string;
  name: string;
  /** Nazwa ikony menu (lucide); null/brak = ikona domyślna. */
  icon?: string | null;
  /** Kolor ikony (klucz z `MENU_COLORS`); null/brak = kolor domyślny. */
  color?: MenuColor | null;
  /** Nieaktywne menu jest pomijane przy wyborze menu panelu. Brak = aktywne. */
  isActive?: boolean;
  isDefault: boolean;
  version: number;
  updatedAt: string;
}
